import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatINR, formatDate } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import { Edit3, Trash2, Receipt, Search, Download } from "lucide-react";
import { toast } from "sonner";
import Pagination, { usePagination } from "@/components/Pagination";

export default function Vouchers() {
  const [vouchers, setVouchers] = useState([]);
  const [search, setSearch] = useState("");
  const [partyFilter, setPartyFilter] = useState("all");
  const [parties, setParties] = useState([]);
  const [fyOptions, setFyOptions] = useState([]);
  const [fyFilter, setFyFilter] = useState("all");

  // export dialog
  const [exportOpen, setExportOpen] = useState(false);
  const [exportFy, setExportFy] = useState("all");
  const [exportParty, setExportParty] = useState("all");
  const [exportFrom, setExportFrom] = useState("");
  const [exportTo, setExportTo] = useState("");
  const [exportCount, setExportCount] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);

  const load = async () => {
    const { data } = await api.get("/vouchers");
    setVouchers(data);
  };

  useEffect(() => {
    load();
    api.get("/invoices/filters").then(({ data }) => setParties(data.party_client_name || []));
    api.get("/vouchers/fy-options").then(({ data }) => setFyOptions(data || [])).catch(() => {});
  }, []);

  const del = async (voucher_no) => {
    if (!window.confirm(`Delete voucher ${voucher_no}? This will remove all its payments & retentions.`)) return;
    try {
      await api.delete(`/vouchers/${voucher_no}`);
      toast.success(`Voucher ${voucher_no} deleted`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const fyOfDate = (d) => {
    if (!d) return "";
    try {
      const [y, m] = d.split("-").map(Number);
      return m >= 4 ? `${y}-${String(y + 1).slice(-2)}` : `${y - 1}-${String(y).slice(-2)}`;
    } catch { return ""; }
  };

  const filtered = vouchers.filter((v) => {
    if (partyFilter !== "all" && v.party_client_name !== partyFilter) return false;
    if (fyFilter !== "all" && fyOfDate(v.payment_date) !== fyFilter) return false;
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      (v.voucher_no || "").toLowerCase().includes(q) ||
      (v.party_client_name || "").toLowerCase().includes(q) ||
      (v.cheque_no || "").toLowerCase().includes(q) ||
      (v.reference || "").toLowerCase().includes(q)
    );
  });

  const totals = filtered.reduce(
    (acc, v) => ({
      payments: acc.payments + (v.payment_total || 0),
      retentions: acc.retentions + (v.retention_total || 0),
      grand: acc.grand + (v.grand_total || 0),
    }),
    { payments: 0, retentions: 0, grand: 0 }
  );

  const {
    pageItems: pagedVouchers,
    page: vPage, setPage: setVPage,
    pageSize: vPageSize, setPageSize: setVPageSize,
    totalPages: vTotalPages, totalItems: vTotalItems,
  } = usePagination(filtered, "voltledger.vouchers.pageSize");

  useEffect(() => {
    if (!exportOpen) return;
    if (exportFrom && exportTo && exportFrom > exportTo) {
      setExportCount({ count: 0, max: 1000, invalid: true });
      return;
    }
    const params = {};
    if (exportFy !== "all") params.fy = exportFy;
    if (exportParty !== "all") params.party_client_name = exportParty;
    if (exportFrom) params.entry_date_from = exportFrom;
    if (exportTo) params.entry_date_to = exportTo;
    setExportCount(null);
    api.get("/exports/vouchers/count", { params })
      .then(({ data }) => setExportCount(data))
      .catch(() => setExportCount({ count: 0, max: 1000 }));
  }, [exportOpen, exportFy, exportParty, exportFrom, exportTo]);

  const downloadExport = async () => {
    try {
      setExportLoading(true);
      const params = {};
      if (exportFy !== "all") params.fy = exportFy;
      if (exportParty !== "all") params.party_client_name = exportParty;
      if (exportFrom) params.entry_date_from = exportFrom;
      if (exportTo) params.entry_date_to = exportTo;
      const res = await api.get("/exports/vouchers", { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const fySuf = exportFy !== "all" ? `_FY${exportFy.replace("-", "")}` : "";
      const pSuf = exportParty !== "all" ? `_${exportParty.replace(/\s+/g, "_")}` : "";
      const dSuf = (exportFrom || exportTo) ? `_${(exportFrom || "any").replace(/-/g, "")}-${(exportTo || "any").replace(/-/g, "")}` : "";
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.download = `vouchers${fySuf}${pSuf}${dSuf}_${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
      setExportOpen(false);
    } catch (e) {
      let msg = "Export failed";
      if (e.response?.data instanceof Blob) {
        try { const t = await e.response.data.text(); const j = JSON.parse(t); msg = j.detail || msg; } catch {}
      } else if (e.response?.data?.detail) msg = e.response.data.detail;
      toast.error(msg);
    } finally {
      setExportLoading(false);
    }
  };

  const tooMany = exportCount && exportCount.count > exportCount.max;

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-[1700px]">
      <div className="flex items-end justify-between gap-6 flex-wrap mb-8">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-red-600/80">Records</div>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mt-1">Voucher Register</h1>
          <p className="text-sm text-slate-500 mt-2">
            {filtered.length} vouchers · Payments {formatINR(totals.payments)} · Retentions {formatINR(totals.retentions)} · Grand {formatINR(totals.grand)}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Dialog open={exportOpen} onOpenChange={setExportOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-none h-10 border-slate-300" data-testid="voucher-export-btn">
                <Download className="w-4 h-4 mr-1.5" /> Export Excel
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none max-w-md">
              <DialogHeader><DialogTitle className="font-display">Export Vouchers</DialogTitle></DialogHeader>
              <div className="space-y-4 pt-1">
                <div>
                  <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Financial Year (Entry Date)</Label>
                  <Select value={exportFy} onValueChange={setExportFy}>
                    <SelectTrigger className="rounded-none mt-1" data-testid="voucher-export-fy"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All FY</SelectItem>
                      {fyOptions.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Party / Client</Label>
                  <Select value={exportParty} onValueChange={setExportParty}>
                    <SelectTrigger className="rounded-none mt-1" data-testid="voucher-export-party"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Parties</SelectItem>
                      {parties.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Entry Date From</Label>
                    <Input
                      type="date"
                      value={exportFrom}
                      onChange={(e) => setExportFrom(e.target.value)}
                      className="rounded-none mt-1"
                      data-testid="voucher-export-from"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Entry Date To</Label>
                    <Input
                      type="date"
                      value={exportTo}
                      onChange={(e) => setExportTo(e.target.value)}
                      className="rounded-none mt-1"
                      data-testid="voucher-export-to"
                    />
                  </div>
                </div>
                {(exportFrom || exportTo) && (
                  <button
                    type="button"
                    onClick={() => { setExportFrom(""); setExportTo(""); }}
                    className="text-[10px] uppercase tracking-[0.2em] text-slate-500 hover:text-[#002FA7] underline"
                    data-testid="voucher-export-clear-dates"
                  >
                    Clear date range
                  </button>
                )}
                {exportCount !== null && (
                  <div
                    className={`text-xs px-3 py-2 border ${
                      exportCount.invalid ? "bg-red-50 border-red-200 text-red-800" :
                      tooMany ? "bg-red-50 border-red-200 text-red-800" :
                      exportCount.count === 0 ? "bg-slate-50 border-slate-200 text-slate-600" :
                      "bg-emerald-50 border-emerald-200 text-emerald-800"
                    }`}
                    data-testid="voucher-export-info"
                  >
                    {exportCount.invalid
                      ? "Entry Date From must be on or before Entry Date To."
                      : exportCount.count === 0
                      ? "No vouchers match these filters."
                      : tooMany
                      ? `${exportCount.count} rows match. Maximum allowed per download is ${exportCount.max}. Narrow FY, Party, or Entry Date range.`
                      : `${exportCount.count} of max ${exportCount.max} rows will be exported.`}
                  </div>
                )}
                <div className="text-[10px] text-slate-400">
                  Columns: SN, Voucher #, Entry Date, Payment Receipt Date, Party, Mode, Cheque/UTR, Invoices, Payment & Retention totals, Grand, System Created On, Remark.
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setExportOpen(false)} className="rounded-none">Cancel</Button>
                <Button
                  onClick={downloadExport}
                  disabled={exportLoading || !exportCount || exportCount.count === 0 || tooMany || exportCount.invalid}
                  className="bg-[#002FA7] hover:bg-[#00227A] rounded-none"
                  data-testid="voucher-export-download"
                >
                  <Download className="w-4 h-4 mr-1.5" />
                  {exportLoading ? "Downloading…" : "Download"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Link
            to="/bulk-payment"
            className="bg-[#002FA7] hover:bg-[#00227A] text-white rounded-none h-10 px-4 inline-flex items-center"
            data-testid="new-voucher-link"
          >
            <Receipt className="w-4 h-4 mr-1.5" /> New Voucher
          </Link>
        </div>
      </div>

      <div className="bg-white border border-slate-200 p-4 mb-6 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[260px]">
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">Search</div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
            <Input
              placeholder="Voucher no, party, cheque, reference…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="rounded-none pl-9"
              data-testid="voucher-search"
            />
          </div>
        </div>
        <div className="w-40">
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">FY (Entry)</div>
          <Select value={fyFilter} onValueChange={setFyFilter}>
            <SelectTrigger className="rounded-none" data-testid="voucher-fy-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All FY</SelectItem>
              {fyOptions.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-56">
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">Party</div>
          <Select value={partyFilter} onValueChange={setPartyFilter}>
            <SelectTrigger className="rounded-none" data-testid="voucher-party-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Parties</SelectItem>
              {parties.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Voucher</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Entry Date</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Payment Receipt Date</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Party</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Mode</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Cheque / UTR</th>
              <th className="text-right px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500" title="Voucher Amount = cash payments + retentions released">Payments</th>
              <th className="text-right px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Retentions</th>
              <th className="text-right px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Grand</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={10} className="text-center text-sm text-slate-400 py-16">
                <Receipt className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                No vouchers yet. Create one from Bulk Payment.
              </td></tr>
            )}
            {pagedVouchers.map((v) => (
              <tr key={v.voucher_no} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`voucher-row-${v.voucher_no}`}>
                <td className="px-4 py-3 font-mono-data font-semibold text-slate-900">
                  <Link to={`/bulk-payment/${encodeURIComponent(v.voucher_no)}`} className="hover:text-[#002FA7]" data-testid={`voucher-link-${v.voucher_no}`}>
                    #{v.voucher_no}
                  </Link>
                </td>
                <td className="px-4 py-3 text-slate-700">{formatDate(v.payment_date)}</td>
                <td className="px-4 py-3 text-slate-700">{v.cheque_date ? formatDate(v.cheque_date) : <span className="text-slate-400">—</span>}</td>
                <td className="px-4 py-3 text-slate-700 font-medium">{v.party_client_name || "—"}</td>
                <td className="px-4 py-3 text-slate-600 text-xs uppercase tracking-wider">{v.mode || "—"}</td>
                <td className="px-4 py-3 text-slate-600 font-mono-data text-xs">{v.cheque_no || v.reference || "—"}</td>
                <td className="px-4 py-3 text-right font-mono-data text-[#002FA7] font-medium" title={`Cash ₹${(v.cash_total ?? v.payment_total ?? 0).toLocaleString('en-IN')} + Released ₹${(v.released_total ?? 0).toLocaleString('en-IN')}`}>{formatINR(v.payment_total)}</td>
                <td className="px-4 py-3 text-right font-mono-data text-amber-700">{formatINR(v.retention_total)}</td>
                <td className="px-4 py-3 text-right font-mono-data font-bold text-slate-900">{formatINR(v.grand_total)}</td>
                <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                  <Link to={`/bulk-payment/${encodeURIComponent(v.voucher_no)}`} className="text-[#002FA7] hover:text-[#00227A] inline-flex items-center gap-1 text-xs font-semibold" data-testid={`edit-voucher-${v.voucher_no}`}>
                    <Edit3 className="w-3.5 h-3.5" /> Edit
                  </Link>
                  <button onClick={() => del(v.voucher_no)} className="text-red-500 hover:text-red-700" data-testid={`del-voucher-${v.voucher_no}`}>
                    <Trash2 className="w-4 h-4 inline" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination
          page={vPage} setPage={setVPage}
          pageSize={vPageSize} setPageSize={setVPageSize}
          totalPages={vTotalPages} totalItems={vTotalItems}
          testId="vouchers-pagination"
        />
      </div>
    </div>
  );
}
